import type { Department, VectorStore } from "../types/api";

interface QueryInputProps {
  prompt: string;
  vectorStore: VectorStore;
  defaultVectorStore: VectorStore;
  departmentId: string;
  departments: Department[];
  onPromptChange: (v: string) => void;
  onVectorStoreChange: (v: VectorStore) => void;
  onDepartmentChange: (id: string, label: string) => void;
  onSubmit: () => void;
}

export function QueryInput({
  prompt,
  vectorStore,
  defaultVectorStore,
  departmentId,
  departments,
  onPromptChange,
  onVectorStoreChange,
  onDepartmentChange,
  onSubmit,
}: QueryInputProps) {
  const canSubmit = prompt.trim().length > 0;
  const selectedDept =
    departments.find((d) => d.id === departmentId) ?? departments[0] ?? null;
  const examples =
    selectedDept?.suggested_questions?.length
      ? selectedDept.suggested_questions
      : [
          "Best approach for DIEP flap breast reconstruction after radiation?",
          "Parkland formula vs Lund-Browder for TBSA in pediatric burns?",
        ];

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && canSubmit) {
      e.preventDefault();
      onSubmit();
    }
  };

  return (
    <main className="body-main input-screen">
      <div className="input-card">
        <p className="eyebrow">Evidence assistant</p>
        <h1 className="input-title">Ask a clinical question</h1>
        <p className="input-subtitle">
          Structured with PICOT or SPICE, then answered from ranked, retraction-checked
          literature.
        </p>

        <label className="field-label" htmlFor="department-select">
          Department
        </label>
        <select
          id="department-select"
          className="dept-select"
          value={departmentId}
          onChange={(e) => {
            const next = departments.find((d) => d.id === e.target.value);
            onDepartmentChange(e.target.value, next?.label ?? e.target.value);
          }}
        >
          {departments.length ? (
            departments.map((d) => (
              <option key={d.id} value={d.id}>
                {d.label}
              </option>
            ))
          ) : (
            <option value={departmentId}>Plastic Surgery</option>
          )}
        </select>
        {selectedDept?.description ? (
          <p className="dept-description">{selectedDept.description}</p>
        ) : null}

        <label className="field-label" htmlFor="clinical-question">
          Clinical question
        </label>
        <textarea
          id="clinical-question"
          className="input-textarea"
          rows={4}
          placeholder="e.g. Breast reconstruction after mastectomy in patients with prior chest-wall radiation"
          value={prompt}
          onChange={(e) => onPromptChange(e.target.value)}
          onKeyDown={handleKeyDown}
        />

        <div className="chip-row">
          {examples.map((ex) => (
            <button
              key={ex}
              type="button"
              className="chip"
              onClick={() => {
                onPromptChange(ex);
                document.getElementById("clinical-question")?.focus();
              }}
            >
              {ex}
            </button>
          ))}
        </div>

        <p className="field-label">Vector store</p>
        <div className="radio-row" role="radiogroup" aria-label="Vector store">
          <label className={`radio-opt${vectorStore === "pinecone" ? " checked" : ""}`}>
            <input
              type="radio"
              name="vector_store"
              value="pinecone"
              checked={vectorStore === "pinecone"}
              onChange={() => onVectorStoreChange("pinecone")}
            />
            Pinecone
            {defaultVectorStore === "pinecone" ? (
              <span className="tag-default">Default</span>
            ) : null}
          </label>
          <label className={`radio-opt${vectorStore === "s3vectors" ? " checked" : ""}`}>
            <input
              type="radio"
              name="vector_store"
              value="s3vectors"
              checked={vectorStore === "s3vectors"}
              onChange={() => onVectorStoreChange("s3vectors")}
            />
            AWS S3 Vectors
          </label>
        </div>

        <button
          type="button"
          className="btn-primary btn-full"
          disabled={!canSubmit}
          onClick={onSubmit}
        >
          Run search →
        </button>
        <p className="helper-caption">Press ⌘/Ctrl + Enter to run</p>
      </div>
    </main>
  );
}
